/**
 * Shared helpers for reading typed errors thrown by the backend API.
 *
 * Errors arrive at the browser through one of two paths:
 *
 *   1. Direct API call (rare in this app) — axios receives the API's body
 *      directly:  err.response.data = { code, message, ... }
 *
 *   2. Through our Nuxt BFF proxy — H3's `createError({ data: ... })` wraps
 *      the original API body inside its own envelope:
 *
 *        err.response.data = {
 *          error: true,
 *          statusCode, statusMessage, message, stack,
 *          data: { code, message, ... }   ← original API body
 *        }
 *
 * These helpers transparently unwrap both shapes so consumers can read
 * `code` / `message` without caring which path delivered the error.
 */

interface ApiErrorBody {
  code?: string
  message?: string
  [key: string]: unknown
}

function looksLikeBffEnvelope(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'statusCode' in value &&
    'data' in value &&
    typeof (value as { data: unknown }).data === 'object'
  )
}

/**
 * Returns the original API error body from any thrown error, unwrapping
 * the BFF H3 envelope when present. Returns an empty object if no body
 * can be located.
 */
export function getApiErrorBody(err: unknown): ApiErrorBody {
  if (!err || typeof err !== 'object') return {}

  const candidates: unknown[] = [
    (err as any)?.response?.data,
    (err as any)?.data,
  ]

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue
    if (looksLikeBffEnvelope(candidate)) {
      return ((candidate as { data: ApiErrorBody }).data) ?? {}
    }
    if ('code' in candidate || 'message' in candidate) {
      return candidate as ApiErrorBody
    }
  }

  return {}
}

export function getApiErrorCode(err: unknown): string | undefined {
  const code = getApiErrorBody(err).code
  return typeof code === 'string' && code.length > 0 ? code : undefined
}

export function getApiErrorMessage(err: unknown, fallback?: string): string {
  const body = getApiErrorBody(err)
  if (typeof body.message === 'string' && body.message.length > 0) {
    return body.message
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message: unknown }).message
    if (typeof message === 'string' && message.length > 0) return message
  }
  return fallback ?? 'An unexpected error occurred.'
}

/**
 * The HTTP status an error carries, whichever client threw it: axios
 * (`response.status`), ofetch (`status`/`statusCode`) or Nuxt's `createError`
 * (`statusCode`). `undefined` when no response came back at all.
 */
export function getApiErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined
  const candidates = [
    (err as { response?: { status?: unknown } }).response?.status,
    (err as { statusCode?: unknown }).statusCode,
    (err as { status?: unknown }).status,
  ]
  return candidates.find((value): value is number => typeof value === 'number')
}

/**
 * Why a page could not show the record it is about.
 *
 * `not-found` is a 404 or a 403: the API answers 404 alike for a record that
 * is gone (a terminated instance) and for one that belongs to another account,
 * and never says which. Anything else — a 5xx, no answer — is `failed`, which
 * a retry may cure.
 */
export type RecordLoadFailure = 'not-found' | 'failed'

export function recordLoadFailure(err: unknown): RecordLoadFailure {
  const status = getApiErrorStatus(err)
  return status === 404 || status === 403 ? 'not-found' : 'failed'
}
